import { useEffect, useState } from 'react';
import { REGIONS, SPONSOR_PACKAGES, packageById, formatUSD } from '@/pages/demo/revenue/components/revenueData';
import StatusBadge from './StatusBadge';
import { ACTIVITY_ICON, LEAD_STAGES, STAGE_TONE, formatDate, leadValueLabel } from './sponsorData';
import type { Lead, LeadStage } from './sponsorData';

type Props = {
  lead: Lead;
  flash: string;
  onClose: () => void;
  onSelectStage: (stage: LeadStage) => void;
  onToggleRegion: (regionId: string) => void;
  onSelectPackage: (packageId: string | null) => void;
  onAddNote: (text: string) => void;
  onToggleTask: (taskId: string) => void;
  onAddTask: (label: string, due: string) => void;
  onConvert: () => void;
};

const cardClass = 'bg-background-50 border border-background-200 rounded-lg p-4 md:p-5';
const labelClass = 'font-label uppercase tracking-widest text-[10px] text-foreground-500';

function Row({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center shrink-0"><i className={icon}></i></span>
      <div className="min-w-0">
        <p className={labelClass}>{label}</p>
        <p className="text-sm text-foreground-950 truncate">{value}</p>
      </div>
    </div>
  );
}

export default function LeadDetail({
  lead,
  flash,
  onClose,
  onSelectStage,
  onToggleRegion,
  onSelectPackage,
  onAddNote,
  onToggleTask,
  onAddTask,
  onConvert,
}: Props) {
  const [note, setNote] = useState('');
  const [taskLabel, setTaskLabel] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const isWon = lead.stage === 'Won';
  const activePackages = SPONSOR_PACKAGES;

  useEffect(() => {
    setNote('');
    setTaskLabel('');
    setTaskDue('');
  }, [lead.id]);

  const submitNote = () => {
    const text = note.trim();
    if (!text) return;
    onAddNote(text);
    setNote('');
  };

  const submitTask = () => {
    const text = taskLabel.trim();
    if (!text) return;
    onAddTask(text, taskDue || '');
    setTaskLabel('');
    setTaskDue('');
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-5 py-4 border-b border-background-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-950 truncate">{lead.company}</h3>
              <StatusBadge tone={STAGE_TONE[lead.stage]}>{lead.stage}</StatusBadge>
            </div>
            <p className="mt-0.5 text-sm text-foreground-600 truncate">{lead.contact} · {lead.title}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-950 hover:bg-background-100 cursor-pointer shrink-0" aria-label="Close detail">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 bg-background-100 border border-background-200 rounded-md px-2.5 py-1 text-xs text-foreground-700">
            <span className="w-4 h-4 flex items-center justify-center text-foreground-500"><i className="ri-user-star-line"></i></span>
            Owner: {lead.owner}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-background-100 border border-background-200 rounded-md px-2.5 py-1 text-xs text-foreground-700">
            <span className="w-4 h-4 flex items-center justify-center text-foreground-500"><i className="ri-share-forward-line"></i></span>
            {lead.source}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-accent-100 border border-accent-300 text-accent-900 rounded-md px-2.5 py-1 text-xs font-semibold">
            {leadValueLabel(lead)}
          </span>
        </div>
        {flash && (
          <p className="mt-3 flex items-start gap-2 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-checkbox-circle-line"></i></span>
            <span>{flash}</span>
          </p>
        )}
      </div>

      <div className="p-4 md:p-5 space-y-4">
        {/* Stage control */}
        <div className={cardClass}>
          <p className={labelClass}>Move stage</p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {LEAD_STAGES.map((s) => {
              const on = lead.stage === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => onSelectStage(s)}
                  className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-foreground-950 border-foreground-950 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contact */}
        <div className={cardClass}>
          <p className={labelClass}>Contact</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Row icon="ri-mail-line" label="Email" value={lead.email} />
            <Row icon="ri-phone-line" label="Phone" value={lead.phone} />
            <Row icon="ri-calendar-line" label="Created" value={formatDate(lead.createdAt)} />
            <Row icon="ri-history-line" label="Last touch" value={formatDate(lead.lastTouch)} />
          </div>
        </div>

        {/* Region + package targeting */}
        <div className={cardClass}>
          <p className={labelClass}>Region targeting</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {REGIONS.map((r) => {
              const on = lead.regionIds.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onToggleRegion(r.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                  {r.city}
                </button>
              );
            })}
          </div>

          <p className={`${labelClass} mt-4`}>Package interest</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {activePackages.map((p) => {
              const on = lead.packageId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelectPackage(on ? null : p.id)}
                  className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-accent-500 border-accent-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-accent-400'}`}
                >
                  {p.name} · {p.price === null ? 'Custom' : formatUSD(p.price)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Next step + convert */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={cardClass}>
            <p className={labelClass}>Next step</p>
            <p className="mt-2 font-heading font-bold text-base text-foreground-950">{lead.nextStep ? formatDate(lead.nextStep) : 'No next step set'}</p>
            <p className="mt-1 text-xs text-foreground-500">Follow-up date for this opportunity.</p>
          </div>
          <div className={cardClass}>
            <p className={labelClass}>Conversion</p>
            {isWon ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-primary-700 font-medium">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-checkbox-circle-fill"></i></span>
                Converted to an active campaign
              </p>
            ) : (
              <button
                type="button"
                onClick={onConvert}
                className="mt-2 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-rocket-2-line"></i></span>
                Convert to campaign
              </button>
            )}
            <p className="mt-2 text-[11px] text-foreground-500">{isWon ? 'Live campaign — simulated.' : 'Simulated — no real campaign is created.'}</p>
          </div>
        </div>

        {/* Tasks */}
        <div className={cardClass}>
          <div className="flex items-center justify-between gap-2">
            <p className={labelClass}>Follow-up tasks</p>
            <span className="font-label text-[10px] text-foreground-500">{lead.tasks.filter((t) => !t.done).length} open</span>
          </div>
          <ul className="mt-3 space-y-2">
            {lead.tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onToggleTask(t.id)}
                  className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 cursor-pointer transition-colors ${t.done ? 'bg-primary-500 border-primary-500 text-background-50' : 'border-background-300 text-transparent hover:border-primary-400'}`}
                  aria-label={t.done ? 'Mark incomplete' : 'Mark complete'}
                >
                  <i className="ri-check-line text-xs"></i>
                </button>
                <span className={`flex-1 text-sm ${t.done ? 'text-foreground-400 line-through' : 'text-foreground-800'}`}>{t.label}</span>
                <span className="font-label text-[10px] uppercase tracking-wide text-foreground-500 whitespace-nowrap">{t.due ? formatDate(t.due) : 'No date'}</span>
              </li>
            ))}
            {lead.tasks.length === 0 && <li className="text-sm text-foreground-500">No tasks yet.</li>}
          </ul>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              value={taskLabel}
              onChange={(e) => setTaskLabel(e.target.value)}
              placeholder="Add a follow-up task"
              className="flex-1 text-sm border border-background-300 rounded-md px-3 py-2 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <input
              type="date"
              value={taskDue}
              onChange={(e) => setTaskDue(e.target.value)}
              className="text-sm border border-background-300 rounded-md px-3 py-2 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <button type="button" onClick={submitTask} className="bg-secondary-500 hover:bg-secondary-600 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-4 py-2 rounded-md whitespace-nowrap cursor-pointer">
              Add
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className={cardClass}>
          <p className={labelClass}>Notes</p>
          <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{lead.notes}</p>
          <div className="mt-3 flex flex-col gap-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Log a note about this sponsor…"
              className="w-full text-sm border border-background-300 rounded-md px-3 py-2 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            ></textarea>
            <button type="button" onClick={submitNote} className="self-start inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
              Add note
            </button>
          </div>
        </div>

        {/* Activity timeline */}
        <div className={cardClass}>
          <p className={labelClass}>Activity timeline</p>
          <ol className="mt-3.5 space-y-3.5">
            {lead.activity.map((a) => (
              <li key={a.id} className="flex gap-3">
                <span className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center shrink-0"><i className={ACTIVITY_ICON[a.kind]}></i></span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm text-foreground-800">{a.text}</p>
                  <p className="text-[11px] text-foreground-500">{formatDate(a.at)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}